/**
 * Single source of truth for "what is wrong with this step right now". Every
 * error display reads these lists: section headers, the issue banner, the
 * Continue/Create snackbars and the Create tooltip.
 *
 * Display only. Step validity is still derived from hasErrors() in ProductStudio.
 */

export const STEP_NAMES = { 1: "Identity", 2: "What the customer gets", 3: "Pricing" };

// Per-line validator keys look like lineItem_0_title. Collapsed by field so five
// untitled lines read as one instruction, not five identical rows.
const LINE_FIELD_MESSAGES = {
  overOriginal: "A price cannot be higher than its original price",
  belowFloor: "A price cannot be discounted more than 20% below its original price",
  id: "Every price line needs an id",
  title: "Give every price line a title",
  amount: "Every price line needs a numeric amount",
};

const lineIssues = (pricingErr) => {
  const found = new Set();
  Object.keys(pricingErr || {}).forEach((k) => {
    const m = /^lineItem_\d+_(\w+)$/.exec(k);
    if (m && LINE_FIELD_MESSAGES[m[1]]) found.add(LINE_FIELD_MESSAGES[m[1]]);
  });
  return Array.from(found);
};

/**
 * ctx is the validation state ProductStudio already computes. Returns
 * { 1: [message], 2: [...], 3: [...] }, each ordered by where its control sits
 * in the form, top to bottom, so the list reads in fix order.
 */
export const buildStepIssues = (ctx) => {
  const {
    basicsErr = {}, shapeErr = {}, pricingErr = {}, fulfillmentPricingErr = null,
    subMissingPlan = false, noFulfillment = false, creditPriceMissing = false,
    negLine = false, negFloor = false, negCredit = false,
  } = ctx || {};

  const step1 = [
    basicsErr.name,
    basicsErr.billingLabel,
    basicsErr.billingPeriod,
    basicsErr.type,
    basicsErr.planTitle,
    basicsErr.planSubtitle,
    basicsErr.features,
  ];

  const step2 = [
    noFulfillment && "Turn on at least one entitlement",
    subMissingPlan && "Select a base plan, or turn Subscription off",
    shapeErr.subscription,
    shapeErr.fulfillment,
    shapeErr.credits,
    negCredit && "Credit amounts cannot be negative",
    shapeErr.webinar,
  ];

  const step3 = [
    ...lineIssues(pricingErr),
    creditPriceMissing && "Set a price per credit for each credit line",
    negLine && "Line amounts cannot be negative",
    pricingErr.lineItems,
    pricingErr.lineItemsSum,
    fulfillmentPricingErr,
    negFloor && "Price floor cannot be negative",
    pricingErr.minPrice,
    pricingErr.basePrice,
    pricingErr.currency,
    pricingErr.migrationDeadline,
  ];

  const clean = (list) => Array.from(new Set(list.filter(Boolean)));
  return { 1: clean(step1), 2: clean(step2), 3: clean(step3) };
};

// The count phrase, in one place: header, banner heading and both snackbars.
export const issueCount = (n) => `${n} issue${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} attention`;

// One line for a snackbar or collapsed header; the banner carries the full list.
export const issueSummary = (issues) =>
  issues.length === 1 ? issues[0] : issueCount(issues.length);

// [1, 3] -> "Identity and Pricing"
// [1, 2, 3] -> "Identity, What the customer gets and Pricing"
export const joinStepNames = (steps) => {
  const names = steps.map((n) => STEP_NAMES[n]).filter(Boolean);
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};
